<template>
  <UppyUploaderWrapper
    :uppyId="uppyId"
    :metadata="metadata"
    :height="height"
    :document-type-id="documentTypeId"
    :upload-success="uploadSuccess"
    :upload-complete="uploadComplete"
    :contact-us="contactUs"
    @errorMessageUpdate="setErrorMessage"
    @uploadComplete="handleUploadComplete"
    @fileAdded="resetUploadedFiles"
  >
    <div class="file-upload">
      <div class="dashboard-wrapper">
        <div class="drag-drop-area" :id="uppyId"></div>
        <div class="filename-wrapper">
          <div class="row" v-for="(file, index) in successfulUploads" :key="index">
            <div class="col name">
              <font-awesome-icon class="icon complete" :icon="['fas', 'circle-check']" />&nbsp;{{
                file?.name
              }}
            </div>
          </div>
        </div>
      </div>
      <div
        class="opal-StatusBar is-waiting"
        :class="successfulUploads.length > 0 ? 'is-complete' : ''"
      >
        <div
          class="opal-StatusBar-progress"
          :class="successfulUploads.length > 0 ? 'is-complete' : ''"
          role="progressbar"
          aria-label="100%"
          aria-valuetext="100%"
          aria-valuemin="0"
          aria-valuemax="100"
          style="width: 100%"
        ></div>
        <div class="opal-StatusBar-content" title="Upload Successful"></div>
        <div class="opal-StatusBar-actions"></div>
        <div class="opal-Dashboard-Item-fileInfoAndButtons" v-show="successfulUploads?.length > 0">
          <div class="opal-Dashboard-Item-fileInfo">
            <div class="opal-Dashboard-Item-fileName">
              <div class="opal-Dashboard-Item-name" :title="fileName">
                {{ fileName }}
              </div>
              <div class="opal-Dashboard-Item-status">
                <div class="opal-Dashboard-Item-statusSize">
                  {{ fileSize }}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="opal-Dashboard-Item-actionWrapper"></div>
      </div>
    </div>
  </UppyUploaderWrapper>
</template>
<script setup lang="ts">
import { ref, defineProps, computed, defineEmits, reactive, watch } from "vue";
import { useStore } from "vuex";
import useFormatter from "@/use/formatter";
import UppyUploaderWrapper from "@/components/shared/UppyUploaderWrapper.vue";
import { DocumentEntityTypes } from "@/models/enums";

const props = defineProps({
  name: { type: String },
  applicationId: { type: Number },
  documentTypeId: { type: Number, default: 23 },
  applicant: { type: Object },
  height: { type: Number, default: 400 },
  uploadSuccess: { type: Boolean, required: false },
  uploadComplete: { type: Boolean, required: false },
});

const { formatBytes } = useFormatter();

const emit = defineEmits([
  "getDocuments",
  "uploadSuccess",
  "errorMessageUpdated",
  "cancel",
  "resetForm",
]);
const store = useStore();
let uploadedFiles = reactive([] as any[]);
const fileUploadComplete = ref<boolean>(false);
const uppyId = computed<string>(() => `uppy_${props?.name}`);
const progressBarId = computed<string>(() => `uppy_${props?.name}_progressBar`);
const statusBarId = computed<string>(() => `uppy_${props?.name}_statusBar`);
const successfulUploads = computed(() => store.getters["genericFileUploaderModule/uploadedFiles"]);
const fileName = computed(() =>
  successfulUploads.value?.length > 0
    ? successfulUploads.value[successfulUploads.value?.length - 1].name
    : undefined,
);
const fileSize = computed(() =>
  successfulUploads.value?.length > 0
    ? successfulUploads.value[successfulUploads.value?.length - 1]?.size
      ? `${formatBytes(successfulUploads.value[successfulUploads.value?.length - 1]?.size)}`
      : successfulUploads.value[successfulUploads.value?.length - 1]?.size
    : undefined,
);
const oidcUser = computed(() => store.getters["oidcStore/oidcUser"]);
const contactUs = computed(() => store.getters["contactUs"]);
const errorMessageText = ref<string>("");
const documentTypeId = computed(() => props.documentTypeId);
const metadata = reactive({
  entityId: props?.applicationId,
  personId: props?.applicant?.id,
  entityTypeId: DocumentEntityTypes.Application,
  userId: oidcUser?.value?.sub,
  documentTypeId: props?.documentTypeId || 23,
  applicantVisibility: true,
});

function setErrorMessage(event: any) {
  errorMessageText.value = event.errorMessage;
}

function resetUploadedFiles() {
  store.dispatch("genericFileUploaderModule/setUploadedFiles", []);
}

function handleUploadComplete(event: any) {
  store.dispatch("genericFileUploaderModule/setUploadedFiles", event.successful);

  fileUploadComplete.value = true;

  const file = event?.successful[0];
  if (file) {
    emit("uploadSuccess", { result: true });
  }
}
</script>
<style lang="scss" scoped>
.dashboard-wrapper {
  .action {
    color: gray;
    font-size: 0.75em;
    text-decoration: none;
    cursor: pointer;
  }

  .col {
    &.end {
      display: flex;
      justify-content: flex-end;
    }
  }
}

.filename-wrapper {
  display: flex;
  align-items: baseline;
}

.col.name {
  width: 75%;
  margin-bottom: 5px;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.3;
  word-wrap: anywhere;
  word-break: break-all;
  color: green;
}

.icon {
  &.complete {
    color: green;
  }
}

.opal-Dashboard-Item-fileInfoAndButtons {
  align-items: flex-start;
  width: 100%;
  padding: 0;
  padding-top: 9px;
}

.opal-Dashboard-Item-fileInfo {
  padding-right: 10px;
}

.opal-Dashboard-Item-fileName {
  display: flex;
  align-items: baseline;
  flex-direction: column;
  color: #757575;
}

.opal-Dashboard-Item-name {
  margin-bottom: 5px;
  font-weight: 500;
  font-size: 12px;
  line-height: 1.3;
  word-wrap: anywhere;
  word-break: break-all;
}

.opal-Dashboard-Item-status {
  color: #757575;
  font-weight: normal;
  font-size: 11px;
  line-height: 1;
}

.opal-Dashboard-Item-statusSize {
  display: inline-block;
  text-transform: uppercase;
  vertical-align: bottom;
  margin-bottom: 5px;
}

.opal-Dashboard-Item-actionWrapper {
  flex-grow: 0;
  flex-shrink: 0;
}

// status bar
.opal-StatusBar {
  position: relative;
  z-index: 1001;
  display: flex;
  height: 46px;
  color: #fff;
  font-weight: 400;
  font-size: 12px;
  line-height: 40px;
  background-color: #fff;
  transition: height 0.2s;
}
.opal-StatusBar.is-waiting {
  height: 0px;
}

.opal-StatusBar.is-waiting.is-complete {
  height: 46px;
}

.opal-StatusBar::before {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: #eaeaea;
  content: "";
}

.opal-StatusBar-progress {
  position: absolute;
  z-index: 1001;
  height: 2px;
  transition: background-color, width 0.3s ease-out;
}
.opal-StatusBar.is-waiting .uppy-StatusBar-progress {
  background-color: #2275d7;
}
.opal-StatusBar-progress.is-complete {
  background-color: #1bb240;
}
.opal-StatusBar.is-complete .uppy-StatusBar-progress {
  background-color: #1bb240;
}
</style>
